import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Calendar, Phone, Mail, Car, Wrench, CheckCircle } from 'lucide-react';
import { leadsAPI } from '../lib/supabase.js';
import { notificationService } from '../services/notificationService';
import '../components/styles/BookServiceModal.css';

const BookServiceModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    vehicle: '',
    serviceType: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const nameParts = formData.name.trim().split(/\s+/);
      const leadData = {
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: formData.email || '',
        phone: formData.phone,
        leadType: 'service',
        leadSource: 'website',
        message: `Vehicle: ${formData.vehicle}\nService: ${formData.serviceType}\n\n${formData.message || ''}`.trim(),
        preferredContactMethod: 'phone'
      };

      await leadsAPI.addLead(leadData);

      // Send email notification to service team
      try {
        await notificationService.sendAppointmentNotification({
          name: formData.name,
          email: formData.email || '',
          phone: formData.phone,
          type: formData.serviceType,
          date: 'ASAP',
          time: 'Customer preferred',
          vehicle: formData.vehicle,
          notes: formData.message || ''
        });
      } catch (emailErr) {
        console.warn('Service email notification failed:', emailErr);
      }

      setSubmitted(true);

      // Fire Meta Pixel Lead event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'book_service', content_category: 'appointment' });
      }

      // Redirect to submission success page for Meta conversion tracking
      navigate('/submission-success?type=service');
    } catch (err) {
      console.error('Form submission error:', err);
      alert('Something went wrong. Please call us at (817) 382-2316.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setFormData({ name: '', phone: '', email: '', vehicle: '', serviceType: '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 10001 }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white form1 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="form1 text-white px-6 py-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6" />
              <h2 className="text-xl font-bold">Book Service</h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-red-100 text-sm mt-1">Schedule your appointment — we'll confirm within 1 hour</p>
        </div>

        {submitted ? (
          <div className="px-6 py-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Received</h3>
            <p className="text-gray-600 mb-6">We'll call or text you shortly to confirm your appointment.</p>
            <button
              onClick={handleClose}
              className="bg-red-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Smith"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
              />
            </div>

            {/* Phone & Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="(817) 555-0123"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@email.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                />
              </div>
            </div>

            {/* Vehicle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle (Year, Make, Model) *</label>
              <input
                type="text"
                name="vehicle"
                value={formData.vehicle}
                onChange={handleChange}
                required
                placeholder="2019 BMW X5"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
              />
            </div>

            {/* Service Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Needed *</label>
              <select
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all bg-white"
              >
                <option value="">Select a service...</option>
                <option value="Oil Change">Oil Change</option>
                <option value="Brake Repair">Brake Repair</option>
                <option value="Check Engine Light / Diagnostics">Check Engine Light / Diagnostics</option>
                <option value="Transmission Service">Transmission Service</option>
                <option value="European Vehicle Service">European Vehicle Service</option>
                <option value="AC / Heating">AC / Heating</option>
                <option value="Suspension / Steering">Suspension / Steering</option>
                <option value="Clutch Repair">Clutch Repair</option>
                <option value="Timing Belt / Chain">Timing Belt / Chain</option>
                <option value="Electrical / Battery">Electrical / Battery</option>
                <option value="Performance Tuning">Performance Tuning</option>
                <option value="Pre-Purchase Inspection">Pre-Purchase Inspection</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Details (optional)</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="3"
                placeholder="Describe the issue or what you need..."
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-red-600 text-white py-3 rounded-lg font-bold text-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending...' : 'Request Appointment'}
            </button>

            <p className="text-xs text-gray-500 text-center">
              Or call us directly: <a href="tel:8173822316" className="text-red-600 font-semibold">(817) 382-2316</a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookServiceModal;
